import {
  SECOND_FACTOR_REASON_LABELS,
  type SecondFactorStatus,
  type TrustedDeviceItem,
} from '@fos/shared/contracts/identity';
import { Monitor, ShieldCheck, Smartphone } from 'lucide-react';
import { Switch } from 'radix-ui';
import { useId, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { ListRow } from '../../../ui/ListRow';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';

/**
 * Ustawienia → Bezpieczeństwo → „Kod z maila przy logowaniu” (Fala 7, czeka na projekt w Figmie): stan drugiego
 * składnika (wymagane przez rolę / włączone / wyłączone), przełącznik dla osób bez wymogu (zmiana wymaga kodu z maila —
 * okno otwiera się samo), zaufane urządzenia z „Odwołaj” i „Odwołaj wszystkie”.
 */
export function TwoFactorSection() {
  const status = trpc.identity.secondFactorStatus.useQuery();
  return (
    <Section className="mt-6" aria-labelledby="two-factor">
      <SectionHeader tone="strong" title={<span id="two-factor">Kod z maila przy logowaniu</span>} />
      {status.isPending ? (
        <ListSkeleton rows={2} />
      ) : status.isError ? (
        <ErrorState compact onRetry={() => void status.refetch()} retrying={status.isFetching} />
      ) : (
        <>
          <SecondFactorState status={status.data} />
          <TrustedDevices devices={status.data.trustedDevices} />
        </>
      )}
    </Section>
  );
}

function SecondFactorState({ status }: { status: SecondFactorStatus }) {
  const utils = trpc.useUtils();
  const id = useId();
  const toggle = trpc.identity.setVoluntarySecondFactor.useMutation({
    onSuccess: (_data, input) => {
      toast.success(input.enabled ? 'Kod z maila włączony' : 'Kod z maila wyłączony');
      void utils.identity.secondFactorStatus.invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const required = status.state === 'required';
  const roleReasons = status.reasons.filter((r) => r !== 'voluntary');

  return (
    <div className="mt-2 flex max-w-[560px] flex-col gap-3">
      <div className="flex items-start gap-3 rounded-xl bg-surface p-4">
        <ShieldCheck size={18} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-fg-muted" />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2 text-body-s font-medium text-fg">
            {required ? 'Wymagany' : status.state === 'enabled' ? 'Włączony' : 'Wyłączony'}
            {required && <Status tone="neutral">wymaga tego Twoja rola</Status>}
          </p>
          <p className="mt-0.5 text-body-s text-fg-secondary">
            {required
              ? `Przy logowaniu na nowym urządzeniu (i co 30 dni) wysyłamy kod na ${status.email}. Powód: ${roleReasons
                  .map((r) => SECOND_FACTOR_REASON_LABELS[r])
                  .join(', ')}.`
              : status.state === 'enabled'
                ? `Przy logowaniu na nowym urządzeniu (i co 30 dni) wysyłamy kod na ${status.email}.`
                : 'Logujesz się samym hasłem. Włącz, żeby przy logowaniu na nowym urządzeniu prosić też o kod z maila.'}
          </p>
        </div>
      </div>
      {!required && (
        <div className="flex items-start gap-4 border-b border-line py-3">
          <div className="min-w-0 flex-1">
            <label htmlFor={id} className="block text-body-s font-medium text-fg">
              Proś o kod z maila przy logowaniu
            </label>
            <p className="mt-0.5 text-caption text-fg-muted">Zmianę potwierdzisz kodem wysłanym na Twój adres.</p>
          </div>
          <Switch.Root
            id={id}
            checked={status.voluntary}
            disabled={toggle.isPending}
            onCheckedChange={(enabled) => toggle.mutate({ enabled })}
            className={cn(
              'relative mt-0.5 inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full bg-line-strong transition-colors',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
              'data-[state=checked]:bg-accent disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <Switch.Thumb
              className={cn(
                'block size-4 translate-x-0.5 rounded-full bg-canvas shadow-xs transition-transform',
                'data-[state=checked]:translate-x-[18px]',
              )}
            />
          </Switch.Root>
        </div>
      )}
    </div>
  );
}

function deviceIcon(label: string) {
  return /android|iphone|ipad|ios|mobile|telefon/i.test(label) ? Smartphone : Monitor;
}

function TrustedDevices({ devices }: { devices: TrustedDeviceItem[] }) {
  const utils = trpc.useUtils();
  const [confirmAll, setConfirmAll] = useState(false);
  const revoke = trpc.identity.revokeTrustedDevice.useMutation({
    onSuccess: () => {
      toast.success('Urządzenie odwołane', { description: 'Przy następnym logowaniu poprosimy o kod.' });
      void utils.identity.secondFactorStatus.invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const revokeAll = trpc.identity.revokeAllTrustedDevices.useMutation({
    onSuccess: () => {
      toast.success('Wszystkie zaufane urządzenia odwołane');
      void utils.identity.secondFactorStatus.invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <div className="mt-8">
      <SectionHeader
        title={<span id="trusted-devices">Zaufane urządzenia</span>}
        action={
          devices.length > 0 ? (
            <Button variant="secondary" size="s" className={TOUCH_BUTTON} onClick={() => setConfirmAll(true)}>
              Odwołaj wszystkie
            </Button>
          ) : undefined
        }
      />
      {devices.length === 0 ? (
        <EmptyState
          compact
          title="Brak zaufanych urządzeń."
          description="Zaznacz „Zaufaj temu urządzeniu” przy wpisywaniu kodu, a przez 30 dni nie poprosimy o kod."
        />
      ) : (
        <div className="border-t border-line">
          {devices.map((d) => {
            const Icon = deviceIcon(d.label);
            return (
              <ListRow
                key={d.id}
                height="l"
                leading={<Icon size={18} strokeWidth={1.75} aria-hidden className="text-fg-muted" />}
                title={d.label}
                description={[
                  d.current ? 'to urządzenie' : d.lastUsedAt ? `ostatnio ${formatRelativePast(d.lastUsedAt)}` : null,
                  `zaufane do ${d.expiresAt.toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw' })}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                trailing={
                  <Button
                    variant="ghost"
                    size="s"
                    className={TOUCH_BUTTON}
                    loading={revoke.isPending && revoke.variables?.id === d.id}
                    onClick={() => revoke.mutate({ id: d.id })}
                    aria-label={`Odwołaj: ${d.label}`}
                  >
                    Odwołaj
                  </Button>
                }
              />
            );
          })}
        </div>
      )}
      <Dialog
        open={confirmAll}
        onOpenChange={setConfirmAll}
        width="s"
        title="Odwołać wszystkie zaufane urządzenia?"
        description={`Przy następnym logowaniu na każdym z nich (${devices.length}) poprosimy o kod z maila. Zalogowane sesje zostają.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmAll(false)} className={TOUCH_BUTTON}>
              Anuluj
            </Button>
            <Button
              variant="primary"
              loading={revokeAll.isPending}
              onClick={() => revokeAll.mutate(undefined, { onSettled: () => setConfirmAll(false) })}
              className={TOUCH_BUTTON}
            >
              Odwołaj wszystkie
            </Button>
          </>
        }
      />
    </div>
  );
}
